import React, { useEffect, useState, useCallback, useMemo, memo } from 'react';
import _ from 'lodash';
import ReactDragListView from 'react-drag-listview';
import PropTypes from 'prop-types';
import { characterLimits } from 'utils/admin-forms';
import { formatDescriptor } from 'utils/admin-products';
import {
  AdminInput,
  AdminFormRow,
  AdminCheckbox,
  AdminSelect,
  AdminCollapsibleContainer,
} from 'shared-components';
import Button, { BUTTON_TYPES } from 'shared-components/Button/Button';
import TaxonomyDropdown from './TaxonomyDropdown/TaxonomyDropdown';
import ShippingRestrictions from './ShippingRestrictions/ShippingRestrictions';
import VariantForm from './VariantForm/VariantForm';

const colorAttributeList = [
  { name: '0', value: 0 },
  { name: '1', value: 1 },
];

const getWordAttributes = (colorAttribute) => {
  const wordAttributesList = [
    { name: '0', value: 0 },
    { name: '1', value: 1 },
    { name: '2', value: 2 },
    { name: '3', value: 3 },
    { name: '4', value: 4 },
  ];
  //we only want to allow 4 word attributes if there is 1 color attribute, otherwise 5 word attributes are allowed
  if (colorAttribute === '1') {
    return wordAttributesList;
  }
  return [...wordAttributesList, { name: '5', value: 5 }];
};

const getDescriptorsList = (variants) =>
  _.map(variants, (variant, index) => {
    return {
      name: formatDescriptor(variant) || `Variable Product ${index + 1}`,
      value: index,
    };
  });

const ProductInformationForm = memo(
  ({
    formData,
    formErrors,
    requiredPublicProductFields,
    requiredPublicVariantFields,
    handleInputChange,
    handleRequiredInputChange,
    handleCheckbox,
    setFormData,
    setRequiredPublicVariantFields,
    resetField,
    variantErrors,
    setVariantErrors,
  }) => {
    const [descriptorsList, setDescriptorsList] = useState([]);
    const {
      brandName,
      productName,
      colorAttributeCount,
      wordAttributesCount,
      defaultVariant,
      shippingRestrictions,
      taxonomyId,
      shortDescription,
      includes,
      features,
      color,
      design,
      size,
      dimensions,
      howToUse,
      ingredients,
      materials,
      techCompatibility,
      careInstructions,
      pao,
      additionalInfo,
      countryOfOrigin,
      dropshipDisclaimer,
      paoDisclaimer,
      cbdDisclaimer,
      productCatalogVariants,
    } = formData;

    const hasColorAttribute =
      colorAttributeCount && colorAttributeCount !== '0';
    const hasWordAttributes =
      wordAttributesCount && wordAttributesCount !== '0';
    const addVariantButtonEnabled = hasColorAttribute || hasWordAttributes;

    //only 4 word attributes are allowed if there is a color attribute
    useEffect(() => {
      if (colorAttributeCount === '1' && wordAttributesCount === '5') {
        setFormData((prevState) => {
          return {
            ...prevState,
            wordAttributesCount: '4',
          };
        });
      }
    }, [colorAttributeCount, wordAttributesCount]);

    useEffect(() => {
      const descriptors = getDescriptorsList(productCatalogVariants);
      setDescriptorsList(descriptors);
    }, [productCatalogVariants]);

    useEffect(() => {
      if (pao) {
        setFormData((prevState) => {
          return {
            ...prevState,
            paoDisclaimer: true,
          };
        });
      } else {
        setFormData((prevState) => {
          return {
            ...prevState,
            paoDisclaimer: false,
          };
        });
      }
    }, [pao]);

    const handleAddVariant = () => {
      setFormData((prevState) => {
        const newState = _.cloneDeep(prevState);
        newState.productCatalogVariants.push({});
        return newState;
      });
    };

    const handleUpdateVariant = useCallback(
      (formData, formErrors, index) => {
        if (!_.isEmpty(formData)) {
          setFormData((prevState) => {
            const newState = _.cloneDeep(prevState);
            newState.productCatalogVariants[index] = formData;
            return newState;
          });
        }
        setVariantErrors((prevState) => {
          const newState = _.cloneDeep(prevState);
          newState[index] = { ...prevState[index], ...formErrors };
          return newState;
        });
      },
      [setVariantErrors, setFormData]
    );

    const deleteVariant = (index) => {
      setFormData((prevState) => {
        const newState = _.cloneDeep(prevState);
        newState.productCatalogVariants.splice(index, 1);
        return newState;
      });
      setVariantErrors((prevState) => {
        const newState = _.cloneDeep(prevState);
        newState.splice(index, 1);
        return newState;
      });
    };

    const removeVariantError = useCallback(
      (fieldName, index) => {
        setVariantErrors((prevState) => {
          const newState = _.cloneDeep(prevState);
          if (newState[index] && newState[index][fieldName]) {
            delete newState[index][fieldName];
          }
          return newState;
        });
        setRequiredPublicVariantFields((prevState) => {
          const newState = _.cloneDeep(prevState);
          if (newState[index] && newState[index][fieldName]) {
            delete newState[index][fieldName];
          }
          return newState;
        });
      },
      [setVariantErrors.setRequiredPublicVariantFields]
    );

    const renderVariants = () =>
      _.map(productCatalogVariants, (variant, index) => {
        const saved = _.has(variant, 'id');
        const formattedDescriptor = formatDescriptor(variant);
        const title = formattedDescriptor
          ? `Variable Product: ${formattedDescriptor}`
          : `Variable Product ${index + 1}`;
        const preventDelete = index === 0 || saved;
        return (
          <AdminCollapsibleContainer
            title={title}
            draggable
            key={index}
            deleteAction={preventDelete ? null : () => deleteVariant(index)}
          >
            <VariantForm
              index={index}
              initialFormData={variant}
              variantErrors={variantErrors[index] || {}}
              requiredPublicVariantFields={
                requiredPublicVariantFields[index] || {}
              }
              colorAttributeCount={colorAttributeCount}
              wordAttributesCount={wordAttributesCount}
              handleUpdateVariant={handleUpdateVariant}
              removeVariantError={removeVariantError}
            />
          </AdminCollapsibleContainer>
        );
      });

    const memoizedVariants = useMemo(() => renderVariants(), [
      productCatalogVariants,
      colorAttributeCount,
      wordAttributesCount,
      handleUpdateVariant,
      removeVariantError,
      deleteVariant,
    ]);

    const reorderVariants = (fromIndex, toIndex) => {
      const oldDescriptors = [...descriptorsList];
      const reorder = (data) => {
        const item = data.splice(fromIndex, 1)[0];
        data.splice(toIndex, 0, item);
      };
      const reorderedVariants = _.cloneDeep(productCatalogVariants);
      reorder(reorderedVariants);
      const newVariants = _.reduce(
        reorderedVariants,
        (result, v, index) => {
          v.order = index + 1;
          result.push(v);
          return result;
        },
        []
      );
      const newDescriptors = getDescriptorsList(reorderedVariants);
      let newDefaultVariant = '';
      if (defaultVariant) {
        newDefaultVariant = _.toString(
          _.findIndex(
            newDescriptors,
            (d) => d.name === oldDescriptors[defaultVariant].name
          )
        );
      }
      setFormData((prevState) => {
        return {
          ...prevState,
          productCatalogVariants: newVariants,
          defaultVariant: newDefaultVariant,
        };
      });

      const reorderedVariantErrors = _.cloneDeep(variantErrors);
      reorder(reorderedVariantErrors);
      setVariantErrors(reorderedVariantErrors);
    };

    const dragProps = {
      onDragEnd(fromIndex, toIndex) {
        // divide by 2 because content includes container and tooltip
        reorderVariants(fromIndex / 2, toIndex / 2);
      },
      nodeSelector: '.draggable-container',
      handleSelector: '.draggable-button',
      lineClassName: 'react-drag-line',
    };
    return (
      <>
        <h4>Shared Product Information</h4>
        <AdminFormRow>
          <AdminInput
            label="Brand Name"
            type="text"
            name="brandName"
            value={brandName || ''}
            onChange={handleRequiredInputChange}
            characterLimit={characterLimits.XSMALL}
            error={
              formErrors.brandName || requiredPublicProductFields.brandName
            }
          />
          <AdminInput
            label="Product Name"
            style={{ width: '588px' }}
            type="text"
            name="productName"
            value={productName || ''}
            characterLimit={characterLimits.XSMALL}
            onChange={handleRequiredInputChange}
            error={
              formErrors.productName || requiredPublicProductFields.productName
            }
          />
        </AdminFormRow>
        <AdminFormRow>
          <AdminSelect
            label="Color Attribute"
            selected={colorAttributeCount || ''}
            selectLabel="Select Amount"
            handleChange={handleInputChange}
            list={colorAttributeList}
            name="colorAttributeCount"
          />
          <AdminSelect
            label="Word Attribute(s)"
            selected={wordAttributesCount || ''}
            selectLabel="Select Amount"
            handleChange={handleInputChange}
            list={getWordAttributes(colorAttributeCount)}
            name="wordAttributesCount"
          />
          <AdminSelect
            label="Default Variant"
            selected={defaultVariant || ''}
            selectLabel="Select Default"
            handleChange={handleRequiredInputChange}
            list={descriptorsList}
            name="defaultVariant"
            error={
              formErrors.defaultVariant ||
              requiredPublicProductFields.defaultVariant
            }
          />
        </AdminFormRow>
        <h4>Shared Shipping Restrictions</h4>
        <AdminFormRow>
          <ShippingRestrictions
            setFormData={setFormData}
            shippingRestrictions={shippingRestrictions}
          />
        </AdminFormRow>
        <AdminCollapsibleContainer title="Shared Product Description">
          <AdminFormRow>
            <TaxonomyDropdown
              handleInputChange={handleRequiredInputChange}
              value={taxonomyId}
              error={
                formErrors.taxonomyId || requiredPublicProductFields.taxonomyId
              }
              resetField={resetField}
            />
          </AdminFormRow>
          <AdminFormRow>
            <AdminInput
              label="Short Description"
              style={{ width: '996px' }}
              type="textArea"
              name="shortDescription"
              value={shortDescription || ''}
              onChange={handleInputChange}
              required
              characterLimit={characterLimits.LARGE}
              error={formErrors.shortDescription}
            />
          </AdminFormRow>
          <AdminFormRow>
            <AdminInput
              label="Includes"
              style={{ width: '486px' }}
              type="textArea"
              name="includes"
              value={includes || ''}
              onChange={handleInputChange}
              characterLimit={characterLimits.XLARGE}
              error={formErrors.includes}
            />
            <AdminInput
              label="Features"
              style={{ width: '486px' }}
              type="textArea"
              name="features"
              value={features || ''}
              onChange={handleInputChange}
              characterLimit={characterLimits.XLARGE}
              error={formErrors.features}
            />
          </AdminFormRow>
          <AdminFormRow>
            <AdminInput
              label="Color"
              style={{ width: '364px' }}
              type="text"
              name="color"
              value={color || ''}
              onChange={handleInputChange}
              characterLimit={characterLimits.XSMALL}
              error={formErrors.color}
            />
            <AdminInput
              label="Design"
              style={{ width: '364px' }}
              type="text"
              name="design"
              value={design || ''}
              onChange={handleInputChange}
              characterLimit={characterLimits.XSMALL}
              error={formErrors.design}
            />
          </AdminFormRow>
          <AdminFormRow>
            <AdminInput
              label="Size"
              style={{ width: '364px' }}
              type="text"
              name="size"
              value={size || ''}
              onChange={handleInputChange}
              characterLimit={characterLimits.XSMALL}
              error={formErrors.size}
            />
            <AdminInput
              label="Dimensions"
              style={{ width: '364px' }}
              type="text"
              name="dimensions"
              value={dimensions || ''}
              onChange={handleInputChange}
              characterLimit={characterLimits.XSMALL}
              error={formErrors.dimensions}
            />
          </AdminFormRow>
          <AdminFormRow>
            <AdminInput
              label="How to Use"
              type="textArea"
              name="howToUse"
              style={{ width: '486px' }}
              value={howToUse || ''}
              onChange={handleInputChange}
              characterLimit={characterLimits.XLARGE}
              error={formErrors.howToUse}
            />
            <AdminInput
              label="Ingredients"
              type="textArea"
              name="ingredients"
              style={{ width: '486px' }}
              value={ingredients || ''}
              characterLimit={characterLimits.XLARGE}
              onChange={handleInputChange}
              error={formErrors.ingredients}
            />
          </AdminFormRow>
          <AdminFormRow>
            <AdminInput
              label="Materials"
              type="text"
              name="materials"
              style={{ width: '486px' }}
              value={materials || ''}
              onChange={handleInputChange}
              characterLimit={characterLimits.MEDIUM}
              error={formErrors.materials}
            />
            <AdminInput
              label="Tech Compatibility"
              type="text"
              name="techCompatibility"
              style={{ width: '486px' }}
              value={techCompatibility || ''}
              onChange={handleInputChange}
              characterLimit={characterLimits.MEDIUM}
              error={formErrors.techCompatibility}
            />
          </AdminFormRow>
          <AdminFormRow style={{ width: '100%' }}>
            <AdminInput
              label="Care Instructions"
              type="text"
              name="careInstructions"
              style={{ width: '690px' }}
              value={careInstructions || ''}
              onChange={handleInputChange}
              characterLimit={characterLimits.LARGE}
              error={formErrors.careInstructions}
            />
            <AdminInput
              label="PAO"
              type="text"
              name="pao"
              value={pao || ''}
              onChange={handleInputChange}
              characterLimit={characterLimits.XSMALL}
              error={formErrors.pao}
            />
          </AdminFormRow>
          <AdminFormRow>
            <AdminInput
              label="Additional Info"
              style={{ width: '486px' }}
              type="textArea"
              name="additionalInfo"
              value={additionalInfo || ''}
              onChange={handleInputChange}
              characterLimit={characterLimits.XLARGE}
              error={formErrors.additionalInfo}
            />
            <AdminInput
              label="Country Of Origin"
              type="text"
              name="countryOfOrigin"
              value={countryOfOrigin || ''}
              onChange={handleInputChange}
              characterLimit={characterLimits.XSMALL}
              error={formErrors.countryOfOrigin}
            />
          </AdminFormRow>
          <AdminFormRow style={{ marginBottom: '0px' }}>
            <AdminCheckbox
              checked={dropshipDisclaimer || false}
              name="dropshipDisclaimer"
              onChange={handleCheckbox}
              label="Add Dropship Disclaimer"
            />
            <AdminCheckbox
              checked={paoDisclaimer || false}
              name="paoDisclaimer"
              onChange={handleCheckbox}
              label="Add PAO Disclaimer"
            />
            <AdminCheckbox
              checked={cbdDisclaimer || false}
              name="cbdDisclaimer"
              onChange={handleCheckbox}
              label="Add CBD Disclaimer"
            />
          </AdminFormRow>
        </AdminCollapsibleContainer>
        <h4>Individual Variable Information</h4>
        <ReactDragListView {...dragProps}>{memoizedVariants}</ReactDragListView>
        <AdminFormRow>
          <span
            data-tip="This button will turn active when an attribute is added at the top of the page."
            data-tip-disable={addVariantButtonEnabled}
          >
            <Button
              buttonType={BUTTON_TYPES.PRIMARY}
              onClick={handleAddVariant}
              disabled={!addVariantButtonEnabled}
            >
              Add A Variable
            </Button>
          </span>
        </AdminFormRow>
      </>
    );
  }
);

ProductInformationForm.propTypes = {
  formData: PropTypes.shape({}).isRequired,
  formErrors: PropTypes.shape({}).isRequired,
  requiredPublicProductFields: PropTypes.shape({}).isRequired,
  requiredPublicVariantFields: PropTypes.arrayOf(PropTypes.shape({}))
    .isRequired,
  handleInputChange: PropTypes.func.isRequired,
  handleRequiredInputChange: PropTypes.func.isRequired,
  handleCheckbox: PropTypes.func.isRequired,
  setFormData: PropTypes.func.isRequired,
  setRequiredPublicVariantFields: PropTypes.func.isRequired,
  resetField: PropTypes.func.isRequired,
  variantErrors: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setVariantErrors: PropTypes.func.isRequired,
};

export default ProductInformationForm;
